import {
  createAccessCodeScannerConfig,
  createHotelScannerConfig,
  type Scanner,
} from '@/config/objects/hotel-access-code-scans';
import { CrudListView } from '@startupquickstart/frontend';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface ScannersListProps {
  /**
   * The hotel ID - used for both the API endpoint (when no accessCodeId)
   * and for navigating to the user detail page
   */
  hotelId?: string;
  /**
   * The access code ID to fetch scanners for
   */
  accessCodeId?: string;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Override the singular entity name shown in the list header/empty state.
   * Defaults to the config's name (e.g. "Scanned User").
   */
  entityName?: string;
  /**
   * Override the plural entity name shown as the list title.
   * Defaults to the config's name (e.g. "Scanned Users").
   */
  entityNamePlural?: string;
}

/**
 * Reusable list of users who have scanned hotel access codes.
 * Can be used on hotel detail page or access code detail page.
 * Clicking a scanner navigates to the user detail page.
 */
export function ScannersList({
  hotelId,
  accessCodeId,
  className,
  entityName,
  entityNamePlural,
}: ScannersListProps) {
  const navigate = useNavigate();

  const getUserUrl = useCallback(
    (scanner: Scanner) => `/hotels/${hotelId}/users/${scanner.user.id}/overview`,
    [hotelId],
  );

  const config = useMemo(() => {
    const baseConfig = accessCodeId
      ? createAccessCodeScannerConfig(accessCodeId)
      : hotelId
        ? createHotelScannerConfig(hotelId)
        : null;

    if (!baseConfig) return null;

    // Apply optional name overrides (e.g. "Guests" on the access code page).
    const named = {
      ...baseConfig,
      entityName: entityName ?? baseConfig.entityName,
      entityNamePlural: entityNamePlural ?? baseConfig.entityNamePlural,
    };

    if (!hotelId) return named;

    // Override view action to navigate to the user detail page
    return {
      ...named,
      listView: {
        ...named.listView,
        actions: {
          ...named.listView.actions,
          view: {
            ...named.listView.actions?.view,
            enabled: true,
            getUrl: getUserUrl,
          },
        },
      },
    };
  }, [hotelId, accessCodeId, getUserUrl, entityName, entityNamePlural]);

  const handleItemClick = useCallback((scanner: Scanner) => {
    if (hotelId) {
      navigate(getUserUrl(scanner));
    }
  }, [hotelId, navigate, getUserUrl]);

  if (!config) {
    return null;
  }

  return (
    <div className={className}>
      <CrudListView<Scanner>
        config={config}
        onItemClick={hotelId ? handleItemClick : undefined}
        getValueFromItem={(scanner) => scanner.user.id}
        getLabelFromItem={(scanner) => scanner.user.email}
      />
    </div>
  );
}
